import { SkillLevel } from '@sportschool/shared-types';

const CACHE_PREFIX = 'sportschool_skill_levels_cache_';
const TTL_MS = 60 * 60 * 1000; // 1 hour

interface SkillLevelCacheEntry {
  timestamp: number;
  levels: SkillLevel[];
}

/**
 * Normalizes a hex color string, ensuring it starts with '#' if valid.
 */
export function getSkillLevelHexColor(color?: string | null): string | null {
  if (!color) return null;
  const trimmed = color.trim();
  if (!trimmed) return null;
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

/**
 * Retrieves cached SkillLevels for a school from LocalStorage if fresh (< 1 hour old).
 */
export function getCachedSkillLevels(schoolId?: string | null): SkillLevel[] | null {
  if (!schoolId) return null;
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${schoolId}`);
    if (!raw) return null;

    const entry: SkillLevelCacheEntry = JSON.parse(raw);
    const now = Date.now();

    if (now - entry.timestamp > TTL_MS) {
      localStorage.removeItem(`${CACHE_PREFIX}${schoolId}`);
      return null;
    }

    return entry.levels;
  } catch {
    return null;
  }
}

/**
 * Saves SkillLevels for a school into LocalStorage with the current timestamp.
 */
export function setCachedSkillLevels(schoolId: string | null | undefined, levels: SkillLevel[]): void {
  if (!schoolId || !levels || levels.length === 0) return;
  try {
    const entry: SkillLevelCacheEntry = {
      timestamp: Date.now(),
      levels,
    };
    localStorage.setItem(`${CACHE_PREFIX}${schoolId}`, JSON.stringify(entry));
  } catch {
    // LocalStorage quota or access error
  }
}
